import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { fetchImsOrgs, type ImsOrg, type ImsProfile, type ImsCatalog } from '../utils/database';
import { getImsUserId } from '../auth/adobeIms';
interface OrgContextValue {
  orgs: ImsOrg[];
  profiles: ImsProfile[];
  profileOrgMap: Record<string, string[]>;
  selectedOrg: ImsOrg | null;
  setSelectedOrg: (org: ImsOrg) => void;
  isLoadingOrgs: boolean;
}
const OrgContext = createContext<OrgContextValue>({
  orgs: [],
  profiles: [],
  profileOrgMap: {},
  selectedOrg: null,
  setSelectedOrg: () => {},
  isLoadingOrgs: false
});
export function OrgProvider({
  children,
  isAdmin
}: {
  children: ReactNode;
  isAdmin: boolean;
}) {
  const [catalog, setCatalog] = useState<ImsCatalog>({
    profiles: [],
    organizations: [],
    profileOrgMap: {},
    count: 0
  });
  const [selectedOrg, setSelectedOrg] = useState<ImsOrg | null>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setIsLoadingOrgs(true);
    fetchImsOrgs().then(data => {
      if (cancelled) return;
      setCatalog(data);
      if (data.organizations.length === 1) {
        setSelectedOrg(data.organizations[0]);
        return;
      }

      // Auto-select the org that belongs to the currently active IMS account.
      const currentUserId = getImsUserId();
      if (currentUserId) {
        const currentOrgIds = data.profileOrgMap[currentUserId] ?? [];
        const activeOrg = data.organizations.find(o => currentOrgIds.includes(o.imsOrgId));
        if (activeOrg) setSelectedOrg(activeOrg);
      }
    }).finally(() => {
      if (!cancelled) setIsLoadingOrgs(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);
  return <OrgContext.Provider value={{
    orgs: catalog.organizations,
    profiles: catalog.profiles,
    profileOrgMap: catalog.profileOrgMap,
    selectedOrg,
    setSelectedOrg,
    isLoadingOrgs
  }} data-id="element-405">
      {children}
    </OrgContext.Provider>;
}
export const useOrg = () => useContext(OrgContext);